import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { IBase } from '@app/common';
import { BaseSelect, BaseSelectProps } from './BaseSelect';
import { useInput } from '@app/hooks/useInput';
import { isDefined } from '@app/helpers/kindOf';

export interface SimpleSelectProps<T> extends Omit<BaseSelectProps<T>, 'isOpen' | 'setIsOpen' | 'formattedLabel' | 'onSelect' | 'onRemove' | 'isActive' | 'control' | 'render' | 'values'> { }

const baseTransform = {
  value: (item: any) => item,
  accessor: (item: any) => item?.['_id'],
  display: (item: any) => item?.['name'],
}

export interface SimpleSelectHandle {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

export const SimpleSelect = forwardRef(<T extends IBase,>(
  {
    label,
    name,
    table,
    ...props
  }: SimpleSelectProps<T>,
  ref: React.Ref<SimpleSelectHandle>
) => {
  const { field } = useInput(name);

  const transform = {
    ...baseTransform,
    ...props?.transform ?? {},
  }

  const [isOpen, setIsOpen] = useState(false);

  // Expose imperative methods to parent via ref
  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
  }), []);

  const values = isDefined(field.value) ? [field.value] : [];

  const onSelect = (item: T) => {
    field.onChange(item);
    setIsOpen(false);
  }

  const isActive = (item: T) => {
    return transform.accessor(field.value) === transform.accessor(item);
  }

  const formattedLabel = () => {
    const value = transform.display!(field.value)

    if (!value) {
      return label!;
    }

    let temp = label + ': ' + value.substring(0, 33);

    temp = temp.length >= 33 ? temp + '...' : temp;

    return temp;
  }

  return (
    <BaseSelect
      name={name}
      label={label}
      table={table}
      {...props}
      values={values}
      render={(item, index) => (
        <BaseSelect.ListItem key={index} compact isActive onPress={() => field.onChange(null)}>
          <BaseSelect.ListItemCol width='100%'>
            <BaseSelect.ListItemText numberOfLines={1} bold>
              {transform.display!(item)}
            </BaseSelect.ListItemText>
          </BaseSelect.ListItemCol>
        </BaseSelect.ListItem>
      )}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      formattedLabel={formattedLabel()}
      onSelect={onSelect}
      isActive={isActive}
    />
  );
});

SimpleSelect.displayName = 'SimpleSelect';
